import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// SERVICES
import CustomerService from "../../../api/customer";

// MATERIAL UI
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SearchFilter from "../../search/searchFilter";
import DateFilter from "../../search/dateFilter";
import { getStartAndEndOfDay } from "../../../extras";
import { ERROR_MESSAGE_SUPPORT } from "../../../common/helpersTxt";
import CustomerOrdersHistoryModal from "../CustomerOrdersHistoryModal";
import CustomerTopProductsList from "./CustomerTopProductsList";

// ICONS
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import CardNumeric from "../../report/cardNumericInfo";
import CardFinance from "../../report/cardFinanceReport";
import { setDateRange } from "../../../utils/filters";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

const initialFilters = getStartAndEndOfDay();

export default function CustomerReport({ customerId }) {
  const theme = useTheme();
  const [ordersCount, setOrdersCount] = useState(0);
  const [averageOrderTotal, setAverageOrderTotal] = useState(0);
  const [productsTotalQuantity, setProductsTotalQuantity] = useState(0);
  const [topProducts, setTopProducts] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const loadReport = useCallback(
    async (filtersData) => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          start_date: filtersData.dateStart,
          end_date: filtersData.dateEnd,
        };
        const { data } = await CustomerService.customerReport(
          customerId,
          params
        );

        setOrdersCount(data.total_orders);
        setAverageOrderTotal(data.average_order_total);
        setProductsTotalQuantity(data.total_quantity);
        setTopProducts(data.top_products);
      } catch (error) {
        console.error(ERROR_MESSAGE_SUPPORT, error);
        setError("Erro ao carregar relatório do cliente");
      } finally {
        setLoading(false);
      }
    },
    [customerId]
  );

  useEffect(() => {
    loadReport(filters);
  }, [filters, loadReport]);

  const search = useCallback(
    async (filtersData) => {
      const filtersObj = setDateRange(filtersData, DateFilter.name, {
        useISOFormat: true,
      });

      setFilters(filtersObj);

      await loadReport(filtersObj);
    },
    [loadReport]
  );

  const handleOrdersListModal = useCallback(() => {
    setShowOrdersModal((prev) => !prev);
  }, []);

  const handleProductsListModal = useCallback(() => {
    setShowProductsModal((prev) => !prev);
  }, []);

  const searchFilters = useMemo(() => [], []);

  const handleRefresh = () => {
    loadReport(filters);
  };

  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  // Format dates for display
  const getFormattedDateRange = () => {
    const startDate = new Date(filters.dateStart);
    const endDate = new Date(filters.dateEnd);

    const formatOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
    return `${startDate.toLocaleDateString(
      "pt-BR",
      formatOptions
    )} - ${endDate.toLocaleDateString("pt-BR", formatOptions)}`;
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          mt: 3,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          overflow: "hidden",
        }}
      >
        {/* Header with date range */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                borderRadius: 1.5,
                p: 0.75,
                width: 32,
                height: 32,
              }}
            >
              <ShoppingCartIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Histórico do Cliente
              </Typography>
              <Chip
                icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
                label={getFormattedDateRange()}
                size="small"
                sx={{ mt: 0.5, borderRadius: 1 }}
              />
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <motion.div
              whileHover="hover"
              whileTap="tap"
              variants={buttonVariants}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon />}
                onClick={toggleFilters}
                sx={{ borderRadius: 2 }}
              >
                Filtros
              </Button>
            </motion.div>

            <motion.div
              whileHover="hover"
              whileTap="tap"
              variants={buttonVariants}
            >
              <Tooltip title="Atualizar relatório">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </motion.div>
          </Stack>
        </Box>

        {/* Filters Section */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${alpha(
                    theme.palette.divider,
                    0.1
                  )}`,
                }}
              >
                <SearchFilter
                  title=""
                  filters={searchFilters}
                  filterDate
                  submit={search}
                />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error Message */}
        {error && (
          <Alert
            severity="error"
            variant="filled"
            icon={<ErrorOutlineIcon />}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ mx: 2, mt: 2, borderRadius: 2 }}
          >
            {error}
          </Alert>
        )}

        {/* Cards Grid */}
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            {/* Ticket Médio */}
            <Grid item xs={12} sm={4}>
              <motion.div variants={itemVariants}>
                {loading ? (
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={180}
                    sx={{ borderRadius: 3 }}
                  />
                ) : (
                  <CardFinance
                    icon="money"
                    label="Ticket Médio"
                    value={averageOrderTotal}
                    colorScheme="success"
                  />
                )}
              </motion.div>
            </Grid>

            {/* Pedidos */}
            <Grid item xs={12} sm={4}>
              <motion.div variants={itemVariants}>
                {loading ? (
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={180}
                    sx={{ borderRadius: 3 }}
                  />
                ) : (
                  <CardNumeric
                    icon="order"
                    label="Pedidos"
                    value={ordersCount}
                    action={handleOrdersListModal}
                    colorScheme="primary"
                  />
                )}
              </motion.div>
            </Grid>

            {/* Produtos Comprados */}
            <Grid item xs={12} sm={4}>
              <motion.div variants={itemVariants}>
                {loading ? (
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={180}
                    sx={{ borderRadius: 3 }}
                  />
                ) : (
                  <CardNumeric
                    icon="takeout"
                    label="Produtos Comprados"
                    value={productsTotalQuantity}
                    action={handleProductsListModal}
                    colorScheme="info"
                  />
                )}
              </motion.div>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Modals */}
      {showOrdersModal && (
        <CustomerOrdersHistoryModal
          show={showOrdersModal}
          customerId={customerId}
          dateStart={filters.dateStart}
          dateEnd={filters.dateEnd}
          closeModal={handleOrdersListModal}
        />
      )}

      <CustomerTopProductsList
        products={topProducts}
        show={showProductsModal}
        closeModal={handleProductsListModal}
      />
    </motion.div>
  );
}
